import React, { useEffect, useRef, useState } from "react";
import kz_logo from "../../../../assets/images/kz_logo.png";
import {
  LegalDocumentBody,
  LegalDocumentMeta,
  LegalFallbackNotice,
  LegalLanguageSelect,
} from "../../CommonComponents/LegalDocument/LegalDocumentContent";
import { formatLegalDate, legalCopy } from "../../../utils/legalDocuments";
import "./LegalPage.css";

const OTHER = {
  privacy: { href: "/terms-of-use", kind: "terms" },
  terms: { href: "/privacy", kind: "privacy" },
};

const withLanguage = (href, language) => (language && language !== "en" ? `${href}?lang=${language}` : href);

const slugify = (text) =>
  text.toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const LegalPage = ({ document = {}, preview = false, lastReviewed = null, homeUrl = "/" }) => {
  const kind = document.kind === "terms" ? "terms" : "privacy";
  const language = document.requested_language || document.language || "en";
  const copy = legalCopy(language);

  const changeLanguage = (next) => {
    const url = new URL(window.location.href);
    url.searchParams.delete("preview");
    if (next === "en") url.searchParams.delete("lang");
    else url.searchParams.set("lang", next);
    window.location.assign(url.toString());
  };
  const contentRef = useRef(null);
  const [sections, setSections] = useState([]);
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    const headings = Array.from(contentRef.current?.querySelectorAll("h2") || []);
    const used = new Set();
    const list = headings.map((heading) => {
      const title = heading.textContent.replace(/\s+/g, " ").trim();
      let id = slugify(title) || "section";
      while (used.has(id)) id = `${id}-x`;
      used.add(id);
      heading.id = id;
      return { id, title };
    });
    setSections(list);

    if (typeof IntersectionObserver === "undefined" || !headings.length) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting);
        if (visible.length) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-80px 0px -70% 0px" }
    );
    headings.forEach((heading) => observer.observe(heading));
    return () => observer.disconnect();
  }, [document.html]);

  const jumpTo = (event, id) => {
    event.preventDefault();
    const target = window.document.getElementById(id);
    if (!target) return;
    target.scrollIntoView({ behavior: "smooth", block: "start" });
    window.history.replaceState(null, "", `#${id}`);
    setActiveId(id);
  };

  const toc = (
    <ol className="kz-legal__toc-list">
      {sections.map((section) => (
        <li key={section.id}>
          <a
            href={`#${section.id}`}
            className={activeId === section.id ? "is-active" : ""}
            onClick={(event) => jumpTo(event, section.id)}
          >
            {section.title}
          </a>
        </li>
      ))}
    </ol>
  );

  const acceptedOn = kind === "privacy" ? formatLegalDate(lastReviewed, language) : null;

  return (
    <div className="kz-legal">
      {preview && (
        <div className="kz-legal__preview">
          Preview of {document.status === "draft" ? "an unpublished draft" : `version ${document.version}`}. Recipients and users do not see this.
        </div>
      )}
      <header className="kz-legal__bar">
        <div className="kz-legal__bar-inner">
          <a href={homeUrl} className="kz-legal__home" aria-label="Kinetizine home">
            <img src={kz_logo} alt="Kinetizine" className="kz-legal__logo" />
          </a>
          <div className="kz-legal__bar-actions">
            {!preview && <LegalLanguageSelect value={language} onChange={changeLanguage} />}
            <button type="button" className="kz-legal__print" onClick={() => window.print()}>
              {copy.print}
            </button>
          </div>
        </div>
      </header>

      <section className="kz-legal__hero">
        <div className="kz-legal__hero-inner">
          <p className="kz-legal__eyebrow">BOX Ventures LLC · Kinetizine</p>
          <h1>{copy.titles[kind]}</h1>
          <p className="kz-legal__lead">{copy.leads[kind]}</p>
        </div>
      </section>

      <div className="kz-legal__body">
        {sections.length > 0 && (
          <nav className="kz-legal__toc" aria-label={copy.onThisPage}>
            <p className="kz-legal__toc-title">{copy.onThisPage}</p>
            {toc}
          </nav>
        )}

        <main className="kz-legal__card">
          {sections.length > 0 && (
            <details className="kz-legal__toc-mobile">
              <summary>{copy.onThisPage}</summary>
              {toc}
            </details>
          )}
          <LegalFallbackNotice document={document} />
          <LegalDocumentMeta document={document} />
          <LegalDocumentBody ref={contentRef} html={document.html} lang={document.language} className="kz-legal__content" />
          {acceptedOn && (
            <p className="kz-legal__accepted">{copy.reviewedOn(acceptedOn)}</p>
          )}
        </main>
      </div>

      <footer className="kz-legal__footer">
        <p>
          © {new Date().getFullYear()} BOX Ventures LLC · Gartenstr. 5, 6300 Zug, Switzerland ·{" "}
          <a href={withLanguage(OTHER[kind].href, language)}>{copy.titles[OTHER[kind].kind]}</a>
        </p>
      </footer>
    </div>
  );
};

export default LegalPage;
